// src/pages/Dashboard.js - Updated version
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Button,
    Alert,
    Spinner,
    Table,
    Badge,
    OverlayTrigger,
    Tooltip
} from 'react-bootstrap';
import {
    PlusCircle,
    ArrowUpRight,
    Trash,
    PencilSquare,
    Lightning,
    FileEarmarkText,
    People,
    ChevronRight,
    InfoCircle,
    CurrencyBitcoin,
    ArrowDownRight,
    ArrowUpRight as ArrowUp,
    GraphUp
} from 'react-bootstrap-icons';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

const Dashboard = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dashboardData, setDashboardData] = useState(null);
    const [flightSheets, setFlightSheets] = useState([]);
    const [deleting, setDeleting] = useState(null);
    const [cryptoInfo, setCryptoInfo] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    // Загрузка данных дашборда
    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);

                // Загружаем общую статистику
                const statsResponse = await api.get('/api/stats/dashboard');
                setDashboardData(statsResponse.data.data);

                // Загружаем полетные листы
                const flightSheetsResponse = await api.get('/api/flight-sheets');
                setFlightSheets(flightSheetsResponse.data.data);

                // Загружаем информацию о криптовалютах
                const cryptoResponse = await api.get('/api/crypto-info');
                setCryptoInfo(cryptoResponse.data.data);
                setLastUpdated(new Date(cryptoResponse.data.lastUpdated));

                setLoading(false);
            } catch (err) {
                setError(
                    err.response?.data?.message ||
                    t('errors.loadingError')
                );
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [t]);

    // Обработчик удаления полетного листа
    const handleDeleteFlightSheet = async (id) => {
        if (!window.confirm(t('flightSheet.deleteConfirm'))) {
            return;
        }

        setDeleting(id);

        try {
            await api.delete(`/api/flight-sheets/${id}`);

            // Обновляем список полетных листов
            setFlightSheets(flightSheets.filter(fs => fs._id !== id));

            // Обновляем общую статистику
            if (dashboardData) {
                setDashboardData({
                    ...dashboardData,
                    totalFlightSheets: dashboardData.totalFlightSheets - 1
                });
            }

            setDeleting(null);
        } catch (err) {
            setError(
                err.response?.data?.message ||
                t('errors.deleteError')
            );
            setDeleting(null);
        }
    };

    // Helper function to find algorithm data by name
    const getAlgorithmData = (algorithmName) => {
        if (!cryptoInfo || !cryptoInfo.miningAlgorithms) return null;
        return cryptoInfo.miningAlgorithms.find(algo => algo.algorithm === algorithmName);
    };

    // Get BTC algorithm data
    const btcAlgo = getAlgorithmData('SHA256ASICBOOST');

    // Функция для форматирования хешрейта
    const formatHashrate = (hashrate) => {
        if (!hashrate) return '0 H/s';

        if (hashrate < 1000) {
            return `${hashrate.toFixed(2)} H/s`;
        } else if (hashrate < 1000000) {
            return `${(hashrate / 1000).toFixed(2)} KH/s`;
        } else if (hashrate < 1000000000) {
            return `${(hashrate / 1000000).toFixed(2)} MH/s`;
        } else if (hashrate < 1000000000000) {
            return `${(hashrate / 1000000000).toFixed(2)} GH/s`;
        } else {
            return `${(hashrate / 1000000000000).toFixed(2)} TH/s`;
        }
    };

    // Parse hashrate from formatted string to raw value in H/s
    const parseHashrate = (hashrateStr) => {
        if (!hashrateStr) return 0;

        const match = hashrateStr.match(/(\d+\.?\d*)\s+(\w+)\/s/);
        if (!match) return 0;

        const value = parseFloat(match[1]);
        const unit = match[2];

        switch (unit) {
            case 'H': return value;
            case 'KH': return value * 1000;
            case 'MH': return value * 1000000;
            case 'GH': return value * 1000000000;
            case 'TH': return value * 1000000000000;
            case 'PH': return value * 1000000000000000;
            case 'EH': return value * 1000000000000000000;
            default: return 0;
        }
    };

    // Calculate estimated earnings based on current hashrate and algorithm data
    const calculateEarnings = (hashrateStr, algorithm) => {
        if (!hashrateStr || !algorithm) return { daily: 0, monthly: 0 };

        // Convert hashrate to H/s
        const hashrateInHs = parseHashrate(hashrateStr);

        // Get BTC per H/day rate from API response
        // This is directly from the 'paying' field in the algorithm data
        const btcPerHsPerDay = algorithm ? algorithm.paying : 5.1756657614974362e-11;

        // Calculate daily earnings based on hashrate in H/s
        const dailyEarnings = hashrateInHs * btcPerHsPerDay;

        return {
            daily: dailyEarnings,
            monthly: dailyEarnings * 30
        };
    };

    // Format crypto with 8 decimal places
    const formatCrypto = (value, decimals = 8) => {
        if (!value) return '0.00000000';
        return value.toFixed(decimals);
    };

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
                <div className="text-center">
                    <div className="spinner-glow mb-4"></div>
                    <p className="text-secondary">{t('common.loading')}</p>
                </div>
            </Container>
        );
    }

    // Calculate estimated earnings based on current hashrate
    const estimatedEarnings = calculateEarnings(dashboardData?.hashrateFormatted || '0 H/s', btcAlgo);

    return (
        <Container fluid className="px-4 py-4 animate-fade-in">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1 className="gradient-text mb-0">{t('dashboard.title')}</h1>
                <Button
                    variant="primary"
                    onClick={() => navigate('/flight-sheets/new')}
                    className="btn-highlight"
                >
                    <PlusCircle className="me-2" />
                    {t('dashboard.createFlightSheet')}
                </Button>
            </div>

            {error && (
                <Alert variant="danger" className="mb-4">
                    {error}
                </Alert>
            )}

            {/* Current Earnings Preview */}
            <Card className="mb-4 shadow-sm">
                <Card.Header>
                    <h5 className="mb-0">Your Mining Performance</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={3} className="border-end">
                            <div className="text-center mb-3">
                                <h6 className="text-secondary mb-2">{t('dashboard.totalHashrate')}</h6>
                                <h2 className="hashrate-value text-glow">{dashboardData?.hashrateFormatted || '0 H/s'}</h2>
                            </div>
                        </Col>
                        <Col md={3} className="border-end">
                            <div className="text-center mb-3">
                                <h6 className="text-secondary mb-2">{t('dashboard.activeWorkers')}</h6>
                                <h2>{dashboardData?.totalActiveWorkers || 0}</h2>
                                {dashboardData?.totalActiveWorkers > 0 && (
                                    <Badge bg="success" pill>{t('common.online')}</Badge>
                                )}
                            </div>
                        </Col>
                        <Col md={3} className="border-end">
                            <div className="text-center mb-3">
                                <h6 className="text-secondary mb-2">Estimated Daily</h6>
                                <h2 className="text-primary">{formatCrypto(estimatedEarnings.daily)} BTC</h2>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="text-center mb-3">
                                <h6 className="text-secondary mb-2">Estimated Monthly</h6>
                                <h2 className="text-success">{formatCrypto(estimatedEarnings.monthly)} BTC</h2>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Список полетных листов */}
            <Card className="glass-card mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">{t('dashboard.flightSheets')}</h5>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => navigate('/flight-sheets/new')}
                    >
                        <PlusCircle className="me-1" /> {t('common.create')}
                    </Button>
                </Card.Header>
                <Card.Body>
                    {flightSheets.length > 0 ? (
                        <div className="table-responsive">
                            <Table hover className="web3-table">
                                <thead>
                                <tr>
                                    <th>{t('common.name')}</th>
                                    <th>{t('flightSheet.minerId')}</th>
                                    <th>Pool</th>
                                    <th>{t('dashboard.hashrate')}</th>
                                    <th>{t('dashboard.workers')}</th>
                                    <th>{t('common.actions')}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {flightSheets.map(flightSheet => {
                                    // Находим статистику для этого полетного листа
                                    const stats = dashboardData?.flightSheetStats.find(
                                        fs => fs.id === flightSheet._id
                                    );

                                    return (
                                        <tr key={flightSheet._id}>
                                            <td>
                                                <strong>{flightSheet.name}</strong>
                                            </td>
                                            <td>
                                                <code>{flightSheet.minerWorker}</code>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <span>{flightSheet.poolHost}:{flightSheet.poolPort}</span>
                                                    <small className="text-muted">
                                                        {flightSheet.poolWorker}
                                                    </small>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="hashrate-value text-glow">
                                                    {stats?.hashrateFormatted || '0 H/s'}
                                                </span>
                                            </td>
                                            <td>
                                                <Badge bg={stats?.activeWorkers ? 'success' : 'secondary'} pill>
                                                    {stats?.activeWorkers || 0}
                                                </Badge>
                                            </td>
                                            <td>
                                                <div className="d-flex">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="me-2"
                                                        title={t('common.view')}
                                                        onClick={() => navigate(`/flight-sheets/${flightSheet._id}`)}
                                                    >
                                                        <ArrowUpRight />
                                                    </Button>
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        className="me-2"
                                                        title={t('common.edit')}
                                                        onClick={() => navigate(`/flight-sheets/${flightSheet._id}/edit`)}
                                                    >
                                                        <PencilSquare />
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        title={t('common.delete')}
                                                        onClick={() => handleDeleteFlightSheet(flightSheet._id)}
                                                        disabled={deleting === flightSheet._id}
                                                    >
                                                        {deleting === flightSheet._id ? (
                                                            <Spinner animation="border" size="sm" />
                                                        ) : (
                                                            <Trash />
                                                        )}
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mb-3 text-secondary">
                                <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M12 8V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M8 12H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <p className="mb-3 text-secondary">{t('dashboard.noFlightSheets')}</p>
                            <Button
                                variant="primary"
                                className="btn-highlight"
                                onClick={() => navigate('/flight-sheets/new')}
                            >
                                <PlusCircle className="me-2" />
                                {t('dashboard.createFlightSheet')}
                            </Button>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Optimization Tips */}
            <Card className="mb-4">
                <Card.Header className="d-flex align-items-center">
                    <InfoCircle className="me-2 text-info" />
                    <h5 className="mb-0">Mining Optimization Tips</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={4} className="mb-3 mb-md-0">
                            <div className="tip-card p-3 rounded" style={{background: "rgba(0,230,118,0.05)"}}>
                                <h6 className="text-primary">Performance Boost</h6>
                                <p className="small text-secondary mb-0">
                                    For optimal BTC mining, consider upgrading to the latest ASIC miners with efficiency ratings under 30 J/TH.
                                </p>
                            </div>
                        </Col>
                        <Col md={4} className="mb-3 mb-md-0">
                            <div className="tip-card p-3 rounded" style={{background: "rgba(0,230,118,0.05)"}}>
                                <h6 className="text-primary">Cost Reduction</h6>
                                <p className="small text-secondary mb-0">
                                    Current optimal time for mining is during off-peak electricity hours (10PM-6AM). Consider scheduling intensive operations.
                                </p>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="tip-card p-3 rounded" style={{background: "rgba(0,230,118,0.05)"}}>
                                <h6 className="text-primary">Pool Selection</h6>
                                <p className="small text-secondary mb-0">
                                    Based on your location, AntPool is currently offering 2% lower fees which could improve your profitability.
                                </p>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {user?.isAdmin && (
                <div className="text-center mt-4">
                    <Button
                        variant="outline-primary"
                        onClick={() => navigate('/admin')}
                        className="btn-highlight"
                    >
                        {t('admin.panel')} <ChevronRight />
                    </Button>
                </div>
            )}
        </Container>
    );
};

export default Dashboard;